import { Sine, TimelineLite } from "gsap";
import * as HLF from "html-living-framework";
import { IOnboardingTarget } from "../GameObjects/IOnboardingTarget";
import { PatchDraggable } from "../GameObjects/PatchDraggable";

export class PatchPalette extends PIXI.Container implements IOnboardingTarget {
    private static SPACING: number = 180;

    private _patches: PatchDraggable[];
    private _radioGroup: HLF.Input.RadioGroup<PatchDraggable>;
    private _isSwapping: boolean;
    private _quiltIndex: number;
    private _swapTimeline: TimelineLite | undefined;

    constructor(pIndex: number, pOnboarding: boolean) {
        super();
        this._isSwapping = false;
        this._quiltIndex = pIndex;

        this._radioGroup = new HLF.Input.RadioGroup<PatchDraggable>();

        this._patches = new Array<PatchDraggable>(3);
        const existing: string[] = new Array<string>();
        for (let i = 0; i < this._patches.length; ++i) {
            this._patches[i] = new PatchDraggable(this._quiltIndex, existing);
            this._patches[i].position.set(0, PatchPalette.SPACING * (-1 + i));
            this._patches[i].onDragBegin.push(this.onPatchDragBegin.bind(this));
            this.addChild(this._patches[i]);
            this._radioGroup.addElement(this._patches[i]);
            existing.push(this._patches[i].patch.data.asset);
        }

        if (pOnboarding) {
            this.hidePatch(0, false, true);
            this.hidePatch(2, false, true);
        }
    }

    public set quiltIndex(pValue: number) {
        this._quiltIndex = pValue;
        for (let i = 0; i < this._patches.length; ++i) {
            this._patches[i].quiltIndex = this._quiltIndex;
        }
    }

    public get patches(): PatchDraggable[] {
        return this._patches;
    }

    public getOnboardingPosition(): PIXI.Point {
        return this._patches[1].getGlobalPosition();
    }

    public getOnboardingRotation(): number {
        return Math.PI * 0.5;
    }

    public swapPatches(pIndex?: number): TimelineLite | undefined {
        if (this._isSwapping === false || pIndex !== undefined) {
            if (this._swapTimeline !== undefined) {
                this._swapTimeline.kill();
            }
            this._swapTimeline = new TimelineLite();

            const isChangingQuilt: boolean = (pIndex !== undefined && pIndex !== this._quiltIndex);
            if (pIndex !== undefined) {
                this.quiltIndex = pIndex;
            }
            this._isSwapping = true;
            const stagger: number = 0.075;

            for (let i = 0; i < this._patches.length; ++i) {
                const index: number = i;
                const patch: PatchDraggable = this._patches[index];
                this._swapTimeline.add(this.hidePatch(index, isChangingQuilt), stagger * index);
                this._swapTimeline.call(() => {
                    const existing: string[] = new Array<string>();
                    for (let j = 0; j < index; ++j) {
                        existing.push(this._patches[j].patch.data.asset);
                    }
                    patch.randomizePatch(existing);
                }, undefined, this, 0.55);
                this._swapTimeline.add(this.showPatch(index), 0.55 + stagger * index);
            }
            this._swapTimeline.call(() => {
                this._isSwapping = false;
            });
            return this._swapTimeline;
        }
        return undefined;
    }

    public onOnboardingComplete(): void {
        this.showPatch(0);
        this.showPatch(2);
    }

    public onAnimateInComplete(): void {
        for (let i = 0; i < this._patches.length; ++i) {
            if (this._patches[i].interactive) {
                PubSub.publishSync(HLF.REGISTER_FOCUSABLE, this._patches[i]);
            }
        }
    }

    private hidePatch(pIndex: number, pOffscreen: boolean, pInstantly: boolean = false): TimelineLite {
        if (this._patches[pIndex].isSelected) {
            this._patches[pIndex].deselect();
        }
        PubSub.publishSync(HLF.UNREGISTER_FOCUSABLE, this._patches[pIndex]);
        this._patches[pIndex].interactive = false;

        const timeline: TimelineLite = new TimelineLite();
        timeline.to(this._patches[pIndex], pInstantly ? 0 : 0.35, {
            x: pOffscreen ? -300 : -40,
            ease: Sine.easeIn,
        }, 0);
        if (pOffscreen === false) {
            timeline.to(this._patches[pIndex], pInstantly ? 0 : 0.35, {
                alpha: 0,
                ease: Sine.easeInOut,
            }, 0);
        }
        return timeline;
    }

    private showPatch(pIndex: number, pInstantly: boolean = false): TimelineLite {
        const timeline: TimelineLite = new TimelineLite();
        timeline.to(this._patches[pIndex], pInstantly ? 0 : 0.35, {
            x: 0,
            ease: Sine.easeOut,
        }, 0);
        timeline.to(this._patches[pIndex], pInstantly ? 0 : 0.35, {
            alpha: 1,
            ease: Sine.easeInOut,
        }, 0);
        timeline.call(() => {
            this._patches[pIndex].interactive = true;
            PubSub.publishSync(HLF.REGISTER_FOCUSABLE, this._patches[pIndex]);
        });
        return timeline;
    }

    private onPatchDragBegin(pPatch: HLF.Input.Draggable): void {
        HLF.PixiUtils.sendToFront(pPatch);
        this._radioGroup.deselect();
    }
}
